// The business portal's shared helpers: talking to the API as the signed-in user, status words, dates in New York time, the miles-recipient choices.
const API = process.env.REACT_APP_BACKEND_URL;

export const token = () => { try { return localStorage.getItem('rideburaq_token'); } catch (e) { return null; } };

// One call: JSON in, JSON out; a failure throws an Error with the server's own words (and .status, .code) so the page can show it as it is.
export async function bizApi(path, { method = 'GET', body } = {}) {
  const t = token();
  const res = await fetch(`${API}/api${path}`, { method, headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }, body: body === undefined ? undefined : JSON.stringify(body) });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const d = data && data.detail;
    const message = typeof d === 'string' ? d : (d && d.message) || (Array.isArray(d) && d[0] && d[0].msg) || 'Something went wrong. Please try again.';
    throw Object.assign(new Error(message), { status: res.status, code: d && d.code });
  }
  return data;
}

// A PDF the browser saves (the request needs the sign-in, so it cannot be a plain link).
export async function downloadPdf(path, filename) {
  const t = token();
  const res = await fetch(`${API}/api${path}`, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
  if (!res.ok) throw new Error('The receipt could not be downloaded.');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export const STATUS_LABEL = { pending: 'Booked', assigned: 'Chauffeur assigned', en_route_to_pickup: 'On the way', arrived: 'Chauffeur arrived', on_trip: 'On trip', completed: 'Completed', cancelled: 'Cancelled', on_hold: 'On hold' };
export const statusLabel = (s) => STATUS_LABEL[s] || (s ? String(s).replace(/_/g, ' ') : 'Booked');
export const isOpen = (s) => !['completed', 'cancelled'].includes(s);

export const money = (n) => (n == null || n === '' ? '—' : `$${Number(n).toFixed(2)}`);

// "Sat, Oct 24 · 9:30 AM" in New York time.
export function whenLabel(raw) {
  if (!raw) return 'Now';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  const f = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const p = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.weekday}, ${p.month} ${p.day} · ${p.hour}:${p.minute} ${p.dayPeriod}`;
}

export const MILES_OPTIONS = [
  { id: 'traveler', label: 'The traveler', hint: 'Miles go to the person who rides. If they have no RideBuraq account yet, they go to the company account.' },
  { id: 'booker', label: 'The booker', hint: 'Miles go to the assistant who books the ride.' },
  { id: 'company', label: 'The company account', hint: 'Miles go to the company account holder.' },
];
export const MILES_TERMS_LINE = 'When a ride is booked for someone else on a business account, the account chooses whether miles go to the booker, the traveler, or the company.';

export const billingLabel = (company) => {
  if (!company) return '';
  if (company.billingMode === 'monthly_invoice') return 'Monthly invoice';
  return company.hasCard && company.card ? `Company card · ${String(company.card.brand || 'card').replace(/^./, (x) => x.toUpperCase())} ending ${company.card.last4}` : 'No company card yet';
};

// The booking page's checkout body -> the body POST /api/business/rides takes. No price goes across: the server prices the ride.
export function businessBody(b, { when, traveler }) {
  const out = {
    vehicle_class: b.ride_type, service: b.service, pickup_name: b.pickup_address, dropoff_name: b.booking_mode === 'hourly' ? undefined : b.dropoff_address,
    pickup_lat: b.pickup_lat, pickup_lng: b.pickup_lng, dropoff_lat: b.dropoff_lat, dropoff_lng: b.dropoff_lng, hours: b.hours || undefined,
    scheduled_at: when ? `${when.date}T${when.time}` : undefined,
    flight_number: b.flight_number || undefined, pickup_terminal: b.pickup_terminal, dropoff_terminal: b.dropoff_terminal, dropoff_airline: b.dropoff_airline,
    meet_greet: !!b.meet_greet, sign_name: b.sign_name, rider_note: b.rider_note, note_chips: b.note_chips,
    child_seat: b.child_seats > 0 ? true : undefined, passengers: b.passengers, luggage: b.luggage, preferences: b.preferences,
    ...(traveler.id ? { traveler_id: traveler.id } : { traveler: { name: traveler.name, mobile: traveler.mobile || undefined, email: traveler.email || undefined }, save_traveler: traveler.save !== false }),
  };
  return Object.fromEntries(Object.entries(out).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}
